"use client";

import Grid from "@mui/material/Grid2";
import { Formik } from "formik";

import MDBox from "@/components/atoms/MDBox";
import RecaptchaBadge from "@/components/molecules/RecaptchaBadge";
import { RECAPTCHA_ACTIONS } from "@/constants/recaptcha.constants";
import useAuth from "@/contexts/auth-context";
import useToast from "@/contexts/toast-context";
import useLoading from "@/hooks/use-loading";
import { useRecaptchaVerification } from "@/hooks/useRecaptcha";
import { RegisterMessages } from "@/messages/register/register.messages";

import RegisterForm from "./components/RegisterForm";
import RightContent from "./components/RightContent";
import { buildFormSchema, buildformInitialData, normalizeRegisterData } from "./form-data";
import { RegisterFormData } from "./types";

type RegisterProps = {
  messages: RegisterMessages;
};

type Props = {
  handleSubmit: (formData: RegisterFormData, _unused?: unknown) => Promise<void>;
  messages: RegisterMessages;
  isLoading: boolean;
};

export default function Register({ messages }: RegisterProps) {
  const { register, isRegistering: isLoadingRegister } = useAuth();
  const { showToast } = useToast();
  const { executeAndVerify, isLoading: isRecaptchaLoading } = useRecaptchaVerification();

  const { startLoading, endLoading, isLoading: isLoadingSubmit } = useLoading();

  const handleSubmit = async (formData: RegisterFormData): Promise<void> => {
    startLoading();

    try {
      // Executa e verifica reCAPTCHA
      const isRecaptchaValid = await executeAndVerify(RECAPTCHA_ACTIONS.REGISTER);

      if (!isRecaptchaValid) {
        // Mensagem de erro já foi exibida pelo hook
        endLoading();
        return;
      }

      // Procede com o registro se reCAPTCHA foi validado
      const normalizedData = normalizeRegisterData(formData);
      register({ formData: normalizedData });
    } catch (error) {
      showToast({
        text: "Erro durante o processo de registro. Tente novamente.",
        type: "error",
      });
    } finally {
      endLoading();
    }
  };

  const isLoading = isLoadingRegister || isLoadingSubmit || isRecaptchaLoading;

  return <RecaptchaWrapper handleSubmit={handleSubmit} messages={messages} isLoading={isLoading} />;
}

const RecaptchaWrapper: React.FC<Props> = ({ handleSubmit, messages, isLoading }) => {
  return (
    <>
      <Formik
        initialValues={buildformInitialData()}
        validateOnChange={false}
        onSubmit={(formData: RegisterFormData) => handleSubmit(formData)}
        validationSchema={buildFormSchema(messages)}
      >
        <Grid container sx={{ height: "var(--full-vh)", width: "100%", overflowX: "hidden" }}>
          <Grid size={{ xs: 12, lg: 4.5 }}>
            <MDBox
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              height={"100%"}
              width={"100%"}
              bgColor="primary"
              position={"relative"}
              zIndex={3}
            >
              <RegisterForm isLoading={isLoading} messages={messages} />
            </MDBox>
          </Grid>

          <Grid size={{ xs: 0, lg: 7.5 }} sx={{ justifyContent: "center", alignItems: "center" }}>
            <MDBox height={"100%"}>
              <RightContent />
            </MDBox>
          </Grid>
        </Grid>
      </Formik>
      <RecaptchaBadge />
    </>
  );
};
